import { useCallback } from 'react';

import { CarouselApi } from '@components/ui/Carousel';
import ArrowBack from '@components/ui/icons/ArrowBack';
import ArrowForward from '@components/ui/icons/ArrowForward';
import { type AutoplayType } from 'embla-carousel-autoplay';

import { cn } from '@utils/cn';

interface CarouselArrowButtonProps {
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function CarouselArrowButton({
  onClick,
  disabled,
  children,
  onMouseEnter,
  onMouseLeave,
}: CarouselArrowButtonProps) {
  return (
    <button
      id="feedback-carousel-arrow-button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'flex size-14 items-center justify-center rounded-full border-2 border-white text-white transition-opacity',
        disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-white/10'
      )}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

interface FeedbackCarouselArrowsProps {
  isFirstSlide: boolean;
  isLastSlide: boolean;
  api: CarouselApi | null;
  plugin: AutoplayType | null;
}

export default function FeedbackCarouselArrows({
  isFirstSlide,
  isLastSlide,
  api,
  plugin,
}: FeedbackCarouselArrowsProps) {
  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleMouseEnter = useCallback(() => {
    plugin?.stop();
  }, [plugin]);

  const handleMouseLeave = useCallback(() => {
    plugin?.play();
  }, [plugin]);

  return (
    <div className="mt-10 hidden gap-4 md:flex">
      <CarouselArrowButton
        onClick={scrollPrev}
        disabled={isFirstSlide}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ArrowBack className="size-6" />
      </CarouselArrowButton>
      <CarouselArrowButton
        onClick={scrollNext}
        disabled={isLastSlide}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ArrowForward className="size-6" />
      </CarouselArrowButton>
    </div>
  );
}
